import React, { useEffect } from 'react';
import { Card, Button, Typography, Space, Divider } from 'antd';
import { useNewIntegrationsStore } from '@/store/newIntegration';
import { useEdgesStore } from '@/store/edges';
import ListModeIndicator from './ListModeIndicator';
import ListOutputPreview from './ListOutputPreview';
import { DataRowType } from '@/types/DataRowType';

const { Title, Text } = Typography;

// Create proper mock source array data for list detection
const mockItems = [
  {
    id: "1001",
    name: "John Doe",
    email: "john@example.com",
    status: "active",
    created_at: "2023-01-15T10:30:00Z",
    metadata: {
      tags: ["customer", "premium"],
      score: 85
    }
  },
  {
    id: "1002",
    name: "Jane Smith",
    email: "jane@example.com",
    status: "pending",
    created_at: "2023-02-20T14:45:00Z",
    metadata: {
      tags: ["customer", "trial"],
      score: 72
    }
  },
  {
    id: "1003",
    name: "Alex Johnson",
    email: "alex@example.com",
    status: "active",
    created_at: "2023-03-05T09:15:00Z",
    metadata: {
      tags: ["customer", "premium"],
      score: 91
    }
  }
];

// Create mock source data with items inside
const mockSourceData = {
  items: mockItems,
  pagination: {
    total: 3,
    page: 1,
    limit: 10
  }
};

// Mock edges for the data mapping
const mockEdges = [
  {
    id: "edge1",
    source: "source",
    target: "destination",
    sourceHandle: "source-id",
    targetHandle: "target-user_id",
    sourceDataType: "string",
    targetDataType: "string",
    sourcePath: "id",
    targetPath: "user_id",
    animated: true,
    style: { stroke: '#2178ff', strokeWidth: 2 }
  },
  {
    id: "edge2",
    source: "source",
    target: "destination",
    sourceHandle: "source-name",
    targetHandle: "target-full_name",
    sourceDataType: "string",
    targetDataType: "string",
    sourcePath: "name",
    targetPath: "full_name",
    animated: true,
    style: { stroke: '#2178ff', strokeWidth: 2 }
  },
  {
    id: "edge3",
    source: "source",
    target: "destination",
    sourceHandle: "source-email",
    targetHandle: "target-contact_email",
    sourceDataType: "string",
    targetDataType: "string",
    sourcePath: "email",
    targetPath: "contact_email",
    animated: true,
    style: { stroke: '#2178ff', strokeWidth: 2 }
  }
];

// Create proper query parameters with all required DataRowType fields
const createQueries = (): DataRowType[] => {
  return [
    { key: "1" as any, urlKey: "user_id", value: "", description: "User ID", active: true },
    { key: "2" as any, urlKey: "full_name", value: "", description: "Full Name", active: true },
    { key: "3" as any, urlKey: "contact_email", value: "", description: "Contact Email", active: true },
    { key: "4" as any, urlKey: "status", value: "", description: "Status", active: true }
  ];
};

const ListModeDemo = () => {
  const setNewIntegration = useNewIntegrationsStore(state => state.setNewIntegration);
  const setEdges = useEdgesStore(state => state.setEdges);
  
  // Setup demo data on component mount
  useEffect(() => {
    // Setup mock integration data
    setNewIntegration({
      name: "Demo Integration",
      integrationName: "demo-integration",
      source: {
        variables: mockItems, // Use the array directly for the variables
        connectorType: "0",
        requestMethod: "GET",
        body: {
          rawContent: JSON.stringify(mockSourceData)
        }
      },
      destination: {
        connectorType: "0",
        requestMethod: "POST",
        queries: createQueries()
      }
    });
    
    // Setup mock edges
    setEdges(mockEdges);
  }, [setNewIntegration, setEdges]);
  
  // Different demo data examples
  const loadSimpleDemoData = () => {
    setNewIntegration({
      name: "Demo Integration",
      integrationName: "demo-integration",
      source: {
        variables: mockItems, // Use the array directly for the variables
        connectorType: "0",
        requestMethod: "GET",
        body: {
          rawContent: JSON.stringify(mockSourceData)
        },
        isList: true,
        listPath: "items",
        listConfig: {
          type: 'simple',
          iterationPath: 'items',
          outputConfig: {
            strategy: 'individual'
          }
        }
      },
      destination: {
        connectorType: "0",
        requestMethod: "POST",
        queries: createQueries()
      }
    });
  };
  
  const loadBulkDemoData = () => {
    setNewIntegration({
      name: "Demo Integration",
      integrationName: "demo-integration",
      source: {
        variables: mockItems, // Use the array directly for the variables
        connectorType: "0",
        requestMethod: "GET",
        body: {
          rawContent: JSON.stringify(mockSourceData)
        },
        isList: true,
        listPath: "items",
        listConfig: {
          type: 'batch',
          iterationPath: 'items',
          batchSize: 10,
          outputConfig: {
            strategy: 'bulk'
          }
        }
      },
      destination: {
        connectorType: "0",
        requestMethod: "POST",
        queries: createQueries()
      }
    });
  };
  
  const loadAggregatedDemoData = () => {
    setNewIntegration({
      name: "Demo Integration",
      integrationName: "demo-integration",
      source: {
        variables: mockItems, // Use the array directly for the variables
        connectorType: "0",
        requestMethod: "GET",
        body: {
          rawContent: JSON.stringify(mockSourceData)
        },
        isList: true,
        listPath: "items",
        listConfig: {
          type: 'pagination',
          iterationPath: 'items',
          paginationConfig: {
            type: 'offset',
            limitParam: 'limit',
            offsetParam: 'offset',
            responsePathToItems: 'items'
          },
          outputConfig: {
            strategy: 'aggregate',
            aggregateField: 'users'
          }
        }
      },
      destination: {
        connectorType: "0",
        requestMethod: "POST",
        queries: createQueries()
      }
    });
  };

  return (
    <Card title="List Mode Demo" style={{ margin: '20px' }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Title level={4}>Data Mapping with List Processing</Title>
        <Text>
          This demo showcases the list processing features for handling array data in integrations.
          Click the buttons below to load different list processing configurations.
        </Text>
        
        <Space style={{ marginTop: '16px' }}>
          <Button type="primary" onClick={loadSimpleDemoData}>
            Simple List Processing
          </Button>
          <Button type="primary" onClick={loadBulkDemoData}>
            Batch Processing
          </Button>
          <Button type="primary" onClick={loadAggregatedDemoData}>
            Aggregated Output
          </Button>
        </Space>
        
        <Divider />
        
        <div style={{ marginTop: '16px' }}>
          <ListModeIndicator sourceData={mockSourceData} />
        </div>
        
        <div style={{ marginTop: '16px' }}>
          <ListOutputPreview sourceData={mockSourceData} />
        </div>
      </Space>
    </Card>
  );
};

export default ListModeDemo; 